import { localizeUrl } from "../helpers";

/* eslint-disable no-use-before-define */
export const edgesToNodes = (resource, type) => {
  if (!resource || !type) return [];
  return resource.edges.map(({ node }) => {
    switch (type) {
      case 'cartline':
        return formatCartLine(node);
      case 'image':
        return formatImage(node);
      case 'product':
        return formatProduct(node);
      case 'variant':
        return formatVariant(node);
      default:
        return null;
    }
  });
};

export const formatBundle = (prods) => {
  const products = edgesToNodes(prods, 'product');
  const activeVariants = products.map((p) =>
    p.variants.find((v) => v.availableForSale),
  );
  const bundleValue = products.reduce((total, product) => {
    const totalPrice =
      total + parseFloat(product.priceRange.minVariantPrice.amount);
    return totalPrice;
  }, 0);

  return {
    products,
    activeVariants: products.map((p) =>
      p.variants.find((v) => v.availableForSale),
    ),
    available: activeVariants.every((v) => v.availableForSale),
    value: {
      amount: bundleValue,
      urrencyCode: window.TMW.localization.currencyCode,
    },
  };
};

export const formatCart = (cart) => {
  const subtotal = cart?.cost?.subtotalAmount || {
    amount: 0,
    currencyCode: window.TMW.localization.currencyCode,
  };
  return {
    ...cart,
    subtotal,
    lines: formatCartLines(edgesToNodes(cart?.lines, 'cartline')),
  };
};

export const formatCartLine = (line) => ({
  ...line,
});

export const formatCartLines = (lines) => {
  if (!lines) return [];

  const processedBundles = [];
  const formattedLines = lines
    .map((line) => {
      const bundleProductsId = line.attributes?.find(
        (attr) => attr.key === '_bundleProductsId',
      );
      // If not a bundle item return the default line object
      if (!bundleProductsId) return formatCartLine(line);
      // If bundle already processed, skip to next line item
      if (processedBundles.includes(bundleProductsId.value)) return null;

      // Get bundle items
      const bundleItems = lines
        .filter((l) => {
          const currentId = l.attributes.find(
            (attr) => attr.key === '_bundleProductsId',
          )?.value;
          if (currentId === bundleProductsId.value) return l;
          return null;
        })
        .map((l) => l);

      // Log bundle ID in processed bundles array
      processedBundles.push(bundleProductsId.value);

      const firstItem = bundleItems[0];
      const bundleData = JSON.parse(
        firstItem.attributes.find((a) => a.key === '_bundleData').value,
      );

      // Return bundle line item
      return {
        merchandise: bundleData.merchandise,
        cost: bundleData.cost,
        quantity: firstItem.quantity,
        children: bundleItems,
      };
    }, [])
    .filter((i) => i);

  return formattedLines;
};

export const formatImage = (image) => ({
  ...image,
});

export const formatProduct = (product) => ({
  ...product,
  variants: edgesToNodes(product.variants, 'variant'),
  images: edgesToNodes(product.images, 'image'),
  hasOnlyDefaultVariant:
    product.options.length === 1 &&
    product.options[0].values[0] === 'Default Title',
});

export const formatVariant = (variant) => ({
  ...variant,
});

/** TODO:
 * make sure this works with banners
 * figure out a way to distinguish differentiate menus with same title (featured 1, featured 2, etc.)
 * one solution would be to add a suffix that gets hidden from the user. e.g. "featured [1]"
 * and we hide anything in square brackets
 */

export const formatMegaMenus = (menus, neededMenus) => {
  if (!menus) return [];
  const formattedMenus = menus?.data?.metaobjects?.edges
    .map((menu) => {
      const menuIdentifier = menu?.node?.fields?.find(
        (field) => field.key === 'menu_identifier',
      )?.value?.trim().toLowerCase();
      const neededMenu = neededMenus.find(
        (m) => m.title.trim().toLowerCase() === menuIdentifier
      );
      if (neededMenu) {
        const formattedMenu = {};
        formattedMenu.handle = menu?.node?.fields.find(
          (n) => n.key === 'menu_heading',
        ).value;
        formattedMenu.url = neededMenu.url === '#' ? false : neededMenu.url;
        formattedMenu.index = neededMenu.index;
        formattedMenu.links = [];
        menu?.node?.fields
          .find((n) => n.key === 'menu_item')
          .references.nodes.map((menuItem) => {
            formattedMenu.links.push({
              url: localizeUrl(menuItem.fields.find((l) => l.key === 'url')?.value) || '/',
              label: menuItem.fields.find((l) => l.key === 'label')?.value || '',
            });
            return '';
          });
        return formattedMenu;
      }
      return '';
    }, [])
    .filter((i) => i);

  return formattedMenus;
};

const getBannerMediaData = (node) => {
  if (!node) return null
  if (node.reference?.image) {
    const url = node.reference.image.originalSrc || null
    const alt = node.reference.image.alt || null
    return ( { type: "image", url, alt } )
  }
  if (node.reference?.sources?.length) {
    return ( { type: "video", id: node.reference.id, sources: node.reference.sources } )
  }
  return null
}

export const formatMegaMenusBanners = (menus, neededMenusBanners) => {
  if (!menus) return [];
  const formattedBanners = menus?.data?.metaobjects?.edges
    .map((menu) => {
      const bannerIdentifier = menu?.node?.fields?.find(
        (field) => field.key === 'banner_identifier',
      )?.value?.trim().toLowerCase();
      const neededMenuBanner = neededMenusBanners.find(
        (m) => m.title?.trim().toLowerCase().replace('#banner', '') === bannerIdentifier
      );
      if (neededMenuBanner) {
        const formattedBanner = {};
        formattedBanner.title = menu?.node?.fields.find(
          (n) => n.key === 'banner_title',
        )?.value;
        formattedBanner.title_color = menu?.node?.fields.find(
          (n) => n.key === 'banner_title_color',
        )?.value?.toLowerCase();
        formattedBanner.media = getBannerMediaData(
          menu?.node?.fields.find((n) => n.key === 'banner_image'),
        );
        formattedBanner.mediaDesktop = getBannerMediaData(
          menu?.node?.fields.find((n) => n.key === 'banner_desktop_image'),
        );
        formattedBanner.mediaMobile = getBannerMediaData(
          menu?.node?.fields.find((n) => n.key === 'banner_mobile_image'),
        );
        formattedBanner.url = localizeUrl(menu?.node?.fields.find(
          (n) => n.key === 'banner_url',
        )?.value);
        formattedBanner.new_tab = menu?.node?.fields.find(
          (n) => n.key === 'open_in_new_tab',
        )?.value;
        return formattedBanner;
      }
      return '';
    }, [])
    .filter((i) => i);

  return formattedBanners;
};

export const formatDiscountConnectors = (connectors) => {
  if (!connectors) return [];
  const formattedConnectors = connectors?.data?.metaobjects?.edges
    .map((connector) => {
      const formattedConnector = [];
      formattedConnector.gwp_product = connector?.node?.fields.find(
        (n) => n.key === 'gwp_product',
      )?.reference;
      formattedConnector.qualify_products_list = connector?.node?.fields.find(
        (n) => n.key === 'qualify_products_list',
      )?.references?.nodes;
      formattedConnector.gift_message = connector?.node?.fields.find(
        (n) => n.key === 'gift_message',
      )?.value;
      formattedConnector.discount_type = connector?.node?.fields.find(
        (n) => n.key === 'discount_type',
      )?.value;
      formattedConnector.gwp_threshold = connector?.node?.fields.find(
        (n) => n.key === 'gwp_threshold',
      )?.value;
      formattedConnector.gwp_threshold_ca = connector?.node?.fields.find(
        (n) => n.key === 'gwp_threshold_ca',
      )?.value;
      formattedConnector.handle = connector?.node?.handle;
      return formattedConnector;
    }, [])
    .filter((i) => i);

  return formattedConnectors;
};

export const formatShoppingGives = (entries) => {
  if (!entries) return [];
  const formattedShoppingGives = entries?.data?.metaobjects?.edges
    .map((entry) => {
      const formattedShoppingGive = [];
      formattedShoppingGive.content = entry?.node?.fields.find(
        (n) => n.key === 'short_description',
      )?.value;
      formattedShoppingGive.icon = entry?.node?.fields.find(
        (n) => n.key === 'icon',
      )?.reference;
      formattedShoppingGive.powered_by_logo = entry?.node?.fields.find(
        (n) => n.key === 'powered_by_logo',
      )?.reference;
      formattedShoppingGive.handle = entry?.node?.handle;
      return formattedShoppingGive;
    }, [])
    .filter((i) => i);

  return formattedShoppingGives;
};

export const formatTryBeforeYouBuy = (data) => {
  if (!data) return null;

    const formattedTryBeforeYouBuy = {}
    formattedTryBeforeYouBuy.id = data.id;
    formattedTryBeforeYouBuy.content = data.fields.find(
      (n) => n.key === 'short_description',
    )?.value;
    formattedTryBeforeYouBuy.explainerText = data.fields.find(
      (n) => n.key === 'explainer_text',
    )?.value;
    formattedTryBeforeYouBuy.detailsHeading = data.fields.find(
      (n) => n.key === 'details_heading',
    )?.value;
    formattedTryBeforeYouBuy.detailsContent = data.fields.find(
      (n) => n.key === 'details_content',
    )?.value;
    formattedTryBeforeYouBuy.icon = data.fields.find(
      (n) => n.key === 'icon',
    )?.reference;
    formattedTryBeforeYouBuy.detailsImage = data.fields.find(
      (n) => n.key === 'details_image',
    )?.reference;
    formattedTryBeforeYouBuy.handle = data.handle;

  return formattedTryBeforeYouBuy;
};

export const formatCartLinesAjax = (lines) => {
  if (!lines) return [];

  const processedBundles = [];
  const formattedLines = lines
    .map((line) => {
      const bundleProductsId = line.properties?._bundleProductsId;
      // If not a bundle item return the default line object
      if (!bundleProductsId) return line;
      // If bundle already processed, skip to next line item
      if (processedBundles.includes(bundleProductsId)) return null;

      // Get bundle items
      const bundleItems = lines
        .filter((l) => {
          const currentId = l.properties?._bundleProductsId;
          if (currentId === bundleProductsId) return l;
          return null;
        })
        .map((l) => l);

      // Log bundle ID in processed bundles array
      processedBundles.push(bundleProductsId);

      const firstItem = bundleItems[0];
      const bundleData = JSON.parse(firstItem.properties._bundleData);

      // Return bundle line item
      return {
        merchandise: bundleData.merchandise,
        cost: bundleData.cost,
        quantity: firstItem.quantity,
        children: bundleItems,
      };
    }, [])
    .filter((i) => i);

  return formattedLines.filter((x) => x);
};
